/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import {
  ChangeEvent,
  forwardRef,
  Ref,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import Grid from "@mui/material/Grid";
import { FormCategory, Ratio } from "@simplyk/common";
import dynamic from "next/dynamic";
import { useSnackbar } from "notistack";
import { Control, FieldValues, Path, useWatch, UseFormRegister, UseFormSetValue } from "react-hook-form";

// eslint-disable-next-line import/no-cycle
import { CroppedFile } from "../Crop/Crop";
import { CropDialog } from "../Crop/CropDialog";
import { useCustomAssetLibraryImages } from "../SelectImageDialog/hooks/useCustomAssetLibraryImages";
import { ImageSelectionResult } from "../SelectImageDialog/types";

// Dynamically import SelectImageDialog to prevent SSR issues with Unsplash tRPC calls
const SelectImageDialog = dynamic(
  () => import("../SelectImageDialog/SelectImageDialog").then((mod) => mod.SelectImageDialog),
  {
    ssr: false,
  }
);

import { getSizeInBite, isHeicFormat } from "./utils";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { LIMIT_10_MB } from "@/common/constants/image";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { isMobileSafari } from "@/common/helpers/parseUserAgent";
import { isRequired } from "@/common/helpers/validators";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useWidth } from "@/common/hooks/useWidth";
import { ImageDimensions } from "@/common/types/image";
import { DashboardCard } from "@/dashboard-design-system/Card/DashboardCard";
import { zeffyCardClasses } from "@/dashboard-design-system/Card/types";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

type Sizes = [100, 100] | [188, 188] | [400, 400] | [800, 800] | [140, 140] | [1024, 576] | [2048, 1152];

export enum ImageType {
  Logo = "logo",
  Banner = "banner",
  Signature = "signature",
  OrganizationLogo = "organizationLogo",
  ItemVisual = "itemVisual",
  TicketBanner = "ticketBanner",
  ProfilePhoto = "profilePhoto",
}

export type RatioSizes =
  | {
      ratio: Ratio.Square;
      sizes: Sizes & ([100, 100] | [188, 188] | [400, 400] | [140, 140] | [800, 800]);
    }
  | {
      ratio: Ratio.Landscape;
      sizes: Sizes & ([1024, 576] | [2048, 1152]);
    };

export interface ImageUploaderHandle {
  setImage: (url: string | null) => void;
}

export interface ImageUploaderProps<T extends FieldValues> {
  className?: string;
  control: Control<T>;
  // Url of the image to display as a preview
  imageUrl?: string | null;
  disabled?: boolean;
  importLabel: string;
  isRequired?: boolean;
  name: Path<T>;
  onChange?: (imageDataUrl: string) => void;
  register: UseFormRegister<T>;
  required?: boolean;
  setDeleted?: () => void;
  setValue: UseFormSetValue<T>;
  "data-test"?: string;
  /**
   * When true, clicking the entry point opens SelectImageDialog. When false (default), goes straight to file picker.
   * Defaults to false so legacy public-canvas callers (no org context) keep their existing OS-file-picker behavior.
   */
  showImageLibrary?: boolean;
  /** When true, the Zeffy Images tab appears inside the dialog. */
  allowZeffyImages?: boolean;
  /** When true, the Unsplash sub-section appears inside the Zeffy Images tab. */
  allowUnsplash?: boolean;
  /** Preferred default tab in the dialog. */
  defaultImageLibraryTab?: "myImages" | "zeffyImages";
  maxDimensions: ImageDimensions | undefined;
  formType?: FormCategory;
  isEditing?: boolean;
  imageType: ImageType;
  handleFillWithPlaceholder?: () => Promise<void>;
  amplitudePayload?: Record<string, unknown>;
  disableCropOnMobile?: boolean;
  /** Whether to show the delete button when an image is present (default: true) */
  showDeleteButton?: boolean;
}

/**
 * @deprecated Use ImageUploader instead
 * This component is used for old flow when we upload image at the same time we update the entity. Now we first upload and then update the entity.
 */
export const LegacyImageUploader = forwardRef(LegacyImageUploaderInner) as unknown as <T extends FieldValues>(
  props: ImageUploaderProps<T> & RatioSizes & RefAttributes<ImageUploaderHandle>
) => React.ReactElement<ImageUploaderProps<T> & RatioSizes>;

export function LegacyImageUploaderInner<T extends FieldValues>(
  {
    "data-test": dataTest,
    disabled = false,
    showImageLibrary = false,
    allowZeffyImages = false,
    allowUnsplash = false,
    defaultImageLibraryTab,
    control,
    imageUrl,
    name,
    onChange: handleChange,
    register,
    required,
    setDeleted,
    setValue,
    isEditing,
    formType,
    amplitudePayload,
    disableCropOnMobile = false,
    showDeleteButton = true,
    className,
    importLabel,
    maxDimensions,
    handleFillWithPlaceholder,
    ratio,
  }: ImageUploaderProps<T> & RatioSizes,
  ref: Ref<ImageUploaderHandle>
): React.ReactElement<ImageUploaderProps<T> & RatioSizes> {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();

  const { organization } = useCurrentUserContext();
  const isCustomAssetLibraryActive = Boolean(organization);

  const { images: customAssetLibraryImages, isLoading: isCustomAssetLibraryLoading } = useCustomAssetLibraryImages({
    enabled: showImageLibrary && isCustomAssetLibraryActive,
  });
  const hasMyImages = !isCustomAssetLibraryLoading && customAssetLibraryImages.length > 0;

  const disableCrop = disableCropOnMobile && isSmallScreen;

  const [fileUrl, setFileUrl] = useState<string | undefined | null>(imageUrl ?? undefined);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [croppedFile, setCroppedFile] = useState<CroppedFile | undefined>(undefined);
  const [actualImageMetaData, setActualImageMetaData] = useState<
    { width: number; height: number; size: number; extension: string } | undefined
  >(undefined);

  const fillImageMetadata = useCallback((url: string) => {
    const isBlob = url.startsWith("data:");
    const extension = (isBlob ? url.split(";")[0].split("image/").pop() : url.split(".").pop()) ?? "none";
    const img = new Image();
    img.src = url;
    img.onload = function () {
      setActualImageMetaData({
        width: img.naturalWidth,
        height: img.naturalHeight,
        extension,
        size: getSizeInBite(url),
      });
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setImage: (url: string | null) => {
        setFileUrl(url || undefined);
      },
    }),
    []
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = useWatch({ control, name });

  const handleCloseCrop = useCallback(() => {
    setIsCropOpen(false);
  }, []);

  const updateFileUrl = useCallback(
    (image: string | undefined | null) => {
      setFileUrl(image);
      if (name && setValue) {
        setValue(name, image as never, { shouldDirty: true });
      }
      if (image) {
        handleChange?.(image);
        fillImageMetadata(image);
      }
    },
    [fillImageMetadata, handleChange, name, setValue]
  );

  // Resets fileUrl only when imageUrl is null, handling banner type switches in Live Form Editor without affecting uploads.
  useEffect(() => {
    if (imageUrl === null) {
      setFileUrl(undefined);
    }
  }, [imageUrl]);

  const handleValidateCrop = useCallback(
    (image: string) => {
      updateFileUrl(image);
      setIsCropOpen(false);
    },
    [updateFileUrl]
  );

  useEffect(() => {
    if (value === undefined) {
      register(name, isRequired(Boolean(required) && !fileUrl, "common.fileRequired"));
    }
  }, [name, fileUrl, required, value, register]);

  const onFileChange = useCallback(
    async (file: File) => {
      const imageDataUrl = URL.createObjectURL(file);
      if (isHeicFormat(file) && !isMobileSafari()) {
        enqueueSnackbar(t("common", "heicConversionFailed"), { vibe: "danger" });
        logAmplitudeEvent(AmplitudeEvents.ImageUploaderHEICConversionFailed, {
          file_type: file.type,
          ...amplitudePayload,
        });
      }
      setCroppedFile({ url: imageDataUrl, type: file.type });

      fillImageMetadata(imageDataUrl);
      setIsCropOpen(true);
    },
    [amplitudePayload, enqueueSnackbar, fillImageMetadata, logAmplitudeEvent, t]
  );

  const handleImageSelection = useCallback(
    (selection: ImageSelectionResult) => {
      const { url } = selection;
      // TODO Add correct unsplash image type
      // Type is not normally sent with the unsplash image
      setCroppedFile({ url, type: "image/png" });
      fillImageMetadata(url);
      setIsCropOpen(true);
    },
    [fillImageMetadata]
  );

  const handleNewImageUpload = useCallback(
    ({ url, mimeType }: { url: string; mimeType: string }) => {
      setCroppedFile({ url, type: mimeType });
      fillImageMetadata(url);
      setIsCropOpen(true);
    },
    [fillImageMetadata]
  );

  const handlePickImage = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.ImageUploaderFlowStarted, {
      is_new_form: !isEditing,
      form_type: formType,
      entry_point: "upload_button",
      ...amplitudePayload,
    });
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  }, [amplitudePayload, formType, isEditing, logAmplitudeEvent]);

  const onInputFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event && event.target ? event.target.files : null;
      if (files && files[0]) {
        if (disableCrop && files[0].size <= LIMIT_10_MB) {
          // Skip the crop modal for small screens due to upload issues
          const file = files[0];
          const reader = new FileReader();
          reader.onloadend = async () => {
            if (typeof reader.result === "string" && getSizeInBite(reader.result) <= LIMIT_10_MB) {
              logAmplitudeEvent(AmplitudeEvents.ImageUploaderCropConfirmed, {
                is_new_form: !isEditing,
                form_type: formType,
                crop_skipped: true,
                ...amplitudePayload,
              });
              updateFileUrl(reader.result);
            }
          };
          reader.readAsDataURL(file);
        } else {
          setIsCropOpen(true);
          onFileChange(files[0]);
        }
      }
      event.target.value = ""; // reset the event allow to re-trigger the onChange, if we close the crop modal and re-choose the same file
    },
    [disableCrop, logAmplitudeEvent, isEditing, formType, amplitudePayload, updateFileUrl, onFileChange]
  );

  const onDelete = useCallback(() => {
    setDeleted?.();
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
    }
    updateFileUrl(null);
    logAmplitudeEvent(AmplitudeEvents.ImageUploaderImageDeleted, {
      is_new_form: !isEditing,
      form_type: formType,
      original_image_size: actualImageMetaData?.size,
      original_image_width: actualImageMetaData?.width,
      original_image_height: actualImageMetaData?.height,
      original_image_extension: actualImageMetaData?.extension,
      ...amplitudePayload,
    });
  }, [
    setDeleted,
    updateFileUrl,
    logAmplitudeEvent,
    isEditing,
    formType,
    actualImageMetaData?.size,
    actualImageMetaData?.width,
    actualImageMetaData?.height,
    actualImageMetaData?.extension,
    amplitudePayload,
  ]);

  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const openSelectImageModal = useCallback(() => {
    if (!showImageLibrary) {
      handlePickImage();
      return;
    }
    const shouldSkipToFilePicker = !allowZeffyImages && !isCustomAssetLibraryLoading && !hasMyImages;
    if (shouldSkipToFilePicker) {
      handlePickImage();
      return;
    }
    logAmplitudeEvent(AmplitudeEvents.CustomizationClickBannerUnsplash, amplitudePayload);
    setSelectImageModalOpen(true);
  }, [
    amplitudePayload,
    logAmplitudeEvent,
    showImageLibrary,
    allowZeffyImages,
    isCustomAssetLibraryLoading,
    hasMyImages,
    handlePickImage,
  ]);

  const closeSelectImageModal = useCallback(() => {
    setSelectImageModalOpen(false);
  }, []);

  const currentDisabled = disabled;
  const ratioValue = ratio === Ratio.Square ? 1 : 16 / 9;

  const [imageUploaderWidth, setImageUploaderWidth] = useState(0);
  const imageUploaderHeight = imageUploaderWidth / ratioValue;
  const [imageUploaderRef, setImageUploaderRef] = useState<HTMLDivElement | null>(null);

  const handleSetImageUploaderRef = useCallback((ref: HTMLDivElement | null) => {
    setImageUploaderRef(ref);
  }, []);

  const { getWidths } = useWidth([imageUploaderRef]);
  useEffect(() => {
    const [columnWidth] = getWidths();
    setImageUploaderWidth(columnWidth);
  }, [getWidths]);

  return (
    <>
      {isCropOpen && (
        <CropDialog
          isOpen={isCropOpen}
          onCrop={handleValidateCrop}
          onClose={handleCloseCrop}
          croppedFile={croppedFile}
          ratio={ratioValue}
          maxDimensions={maxDimensions}
          actualImageMetaData={actualImageMetaData}
          fillImageMetadata={fillImageMetadata}
          handlePickImage={handlePickImage}
          amplitudePayload={{ ...amplitudePayload, is_new_form: !isEditing, form_type: formType }}
        />
      )}
      {selectImageModalOpen && (
        <SelectImageDialog
          isOpen={selectImageModalOpen}
          onClose={closeSelectImageModal}
          onSelectImage={handleImageSelection}
          onNewImageUpload={handleNewImageUpload}
          allowZeffyImages={allowZeffyImages}
          allowUnsplash={allowUnsplash}
          defaultTab={isCustomAssetLibraryActive ? defaultImageLibraryTab : "zeffyImages"}
          customAssetLibraryImages={customAssetLibraryImages}
          isCustomAssetLibraryLoading={isCustomAssetLibraryLoading}
        />
      )}
      <DashboardCard
        variant="outlined"
        fullWidth
        noHover
        className={className}
        data-test={dataTest}
        ref={handleSetImageUploaderRef}
        style={{ height: imageUploaderHeight }}
        sx={(theme) => ({
          [`&.${zeffyCardClasses.root}`]: {
            position: "relative",
            height: "100%",
            backgroundColor: theme.palette.surface.neutral.supershy,
            borderColor: theme.palette.border.neutral.moderate,
            borderStyle: "dashed",
            overflow: "hidden",
            padding: 0,
            "&:hover": {
              backgroundColor: theme.palette.surface.neutral.supershy,
            },
          },
        })}
      >
        <input
          name={name}
          accept="image/*"
          style={{ display: "none" }}
          id={name}
          type="file"
          onChange={onInputFileChange}
          ref={inputRef}
          disabled={currentDisabled}
        />
        <Grid
          container
          sx={{
            cursor: currentDisabled ? "disabled" : "pointer",
            backgroundImage: `url(${fileUrl})`,
            backgroundSize: "cover",
            height: imageUploaderHeight,
          }}
          justifyContent="center"
          alignItems="center"
        >
          {!showImageLibrary && (
            <>
              {!fileUrl && (
                <Grid
                  container
                  justifyContent="center"
                  sx={{ p: 1, position: "relative" }}
                  spacing={1}
                  onClick={handlePickImage}
                >
                  <Grid item>
                    <DashboardButton variant="tonal" vibe="neutral" data-test={`${dataTest}-upload-button`}>
                      {importLabel}
                    </DashboardButton>
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {showImageLibrary && (
            <Grid container justifyContent="center" sx={{ p: 1, position: "relative" }} spacing={1}>
              <Grid item>
                <Grid container spacing={2}>
                  {!fileUrl && (
                    <Grid item container alignItems="center" spacing={1} flexDirection="column" flexWrap="wrap">
                      <Grid item alignSelf="stretch">
                        <DashboardButton
                          variant="tonal"
                          vibe="neutral"
                          onClick={handlePickImage}
                          fullWidth
                          data-test={`${dataTest}-upload-button`}
                        >
                          {importLabel}
                        </DashboardButton>
                      </Grid>
                      {(process.env.NODE_ENV !== "production" || handleFillWithPlaceholder) && (
                        <>
                          <Grid item>
                            <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                              {t("common", "or")}
                            </DashboardTypography>
                          </Grid>
                          <Grid item alignSelf="stretch">
                            <DashboardButton
                              variant="outlined"
                              vibe="neutral"
                              onClick={handleFillWithPlaceholder ?? openSelectImageModal}
                              fullWidth
                            >
                              {handleFillWithPlaceholder
                                ? t("dashboard", "fillWithPlaceholderBanner")
                                : t("common", "selectImage")}
                            </DashboardButton>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {fileUrl && (
            <Grid item container height="100%" alignItems="center" justifyContent="center" spacing={2} padding={1}>
              <Grid item>
                <DashboardButton
                  variant="outlined"
                  vibe="neutral"
                  onClick={showImageLibrary ? openSelectImageModal : handlePickImage}
                  sx={{ backgroundColor: "white" }}
                  data-test={`${dataTest}-replace-button`}
                >
                  {t("common", "replaceFromFile")}
                </DashboardButton>
              </Grid>

              {showDeleteButton && (
                <Grid item>
                  <DashboardButton
                    variant="outlined"
                    vibe="neutral"
                    onClick={onDelete}
                    sx={{ backgroundColor: "white" }}
                    data-test={`${dataTest}-delete-button`}
                  >
                    {t("common", "deleteImage")}
                  </DashboardButton>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </DashboardCard>
    </>
  );
}
